import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCustomerSettings } from "../context/CustomerSettingsContext";
import { CUSTOMER_CURRENCIES } from "../utils/currency";
import { CUSTOMER_LANGUAGES, REGION_FORMATTING_LOCALES } from "../utils/intlLocale";

const GeneralSettingsPage = () => {
  const { t } = useTranslation();
  const {
    currency,
    language,
    loading,
    regionFormattingLocale,
    saveSettings,
  } = useCustomerSettings();
  const [formValues, setFormValues] = useState({
    currency,
    language,
    regionFormattingLocale,
  });
  const [savingField, setSavingField] = useState(null);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    setFormValues({ currency, language, regionFormattingLocale });
  }, [currency, language, regionFormattingLocale]);

  const handleChange = async (field, value) => {
    const previous = formValues;
    const next = { ...formValues, [field]: value };
    setFormValues(next);
    setSavingField(field);
    setError(null);
    try {
      await saveSettings({ [field]: value });
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Failed to update general settings:", err);
      setFormValues(previous);
      setError(t("settings.general.errors.updateFailed"));
    } finally {
      setSavingField(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t("settings.general.title")}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 1 }}>
        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel id="general-language-label">{t("settings.general.language.label")}</InputLabel>
            <Select
              labelId="general-language-label"
              value={formValues.language}
              label={t("settings.general.language.label")}
              onChange={(event) => handleChange("language", event.target.value)}
              disabled={savingField === "language"}
            >
              {CUSTOMER_LANGUAGES.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="general-region-formatting-label">{t("settings.general.regionFormatting.label")}</InputLabel>
            <Select
              labelId="general-region-formatting-label"
              value={formValues.regionFormattingLocale}
              label={t("settings.general.regionFormatting.label")}
              onChange={(event) => handleChange("regionFormattingLocale", event.target.value)}
              disabled={savingField === "regionFormattingLocale"}
            >
              {REGION_FORMATTING_LOCALES.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="general-currency-label">{t("settings.general.currency.label")}</InputLabel>
            <Select
              labelId="general-currency-label"
              value={formValues.currency}
              label={t("settings.general.currency.label")}
              onChange={(event) => handleChange("currency", event.target.value)}
              disabled={savingField === "currency"}
            >
              {CUSTOMER_CURRENCIES.map((code) => (
                <MenuItem key={code} value={code}>{code}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={t("settings.general.snackbar.updated")}
      />
    </Container>
  );
};

export default GeneralSettingsPage;
